import React, { type JSX } from "react";
import { RotateCcw, Search } from "lucide-react";
import type { AdminRecognitionAuditQuery } from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { AdminFilterBar } from "../../shared/ui/admin-filter-bar";

type RecognitionAuditFilterBarProps = {
  locale: AdminDashboardLocale;
  onApply: () => void;
  onReset: () => void;
  queryDraft: AdminRecognitionAuditQuery;
  updateQueryDraft: (patch: Partial<AdminRecognitionAuditQuery>) => void;
};

function controlStyle() {
  return {
    width: "100%",
    minHeight: "40px",
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    background: "#fff",
    color: "#18212b",
    fontSize: "0.92rem",
  } as const;
}

function buttonStyle(tone: "primary" | "ghost") {
  return {
    minHeight: "40px",
    padding: "0 14px",
    borderRadius: "12px",
    border:
      tone === "primary"
        ? "1px solid rgba(30, 64, 175, 0.22)"
        : "1px solid rgba(148, 163, 184, 0.35)",
    background: tone === "primary" ? "#eff6ff" : "#fff",
    color: tone === "primary" ? "#1d4ed8" : "#334155",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  } as const;
}

function textValueToArray(value: string): readonly string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function arrayValueToText(value: readonly string[]): string {
  return value.join(", ");
}

export function RecognitionAuditFilterBar(
  props: RecognitionAuditFilterBarProps,
): JSX.Element {
  const copy = {
    period: pickAdminDashboardLocaleText(props.locale, { en: "Period", ko: "기간" }),
    from: pickAdminDashboardLocaleText(props.locale, { en: "From", ko: "시작" }),
    to: pickAdminDashboardLocaleText(props.locale, { en: "To", ko: "종료" }),
    documentKind: pickAdminDashboardLocaleText(props.locale, { en: "Document kind", ko: "문서 종류" }),
    status: pickAdminDashboardLocaleText(props.locale, { en: "Status", ko: "상태" }),
    outcome: pickAdminDashboardLocaleText(props.locale, { en: "Outcome", ko: "결과" }),
    failedStage: pickAdminDashboardLocaleText(props.locale, { en: "Failed stage", ko: "실패 위치" }),
    errorCode: pickAdminDashboardLocaleText(props.locale, { en: "Error code", ko: "에러 코드" }),
    model: pickAdminDashboardLocaleText(props.locale, { en: "Model", ko: "모델" }),
    accountId: pickAdminDashboardLocaleText(props.locale, { en: "Account ID", ko: "계정 ID" }),
    petId: pickAdminDashboardLocaleText(props.locale, { en: "Pet ID", ko: "반려동물 ID" }),
    apply: pickAdminDashboardLocaleText(props.locale, { en: "Apply filters", ko: "필터 적용" }),
    reset: pickAdminDashboardLocaleText(props.locale, { en: "Reset", ko: "초기화" }),
  };

  return (
    <AdminFilterBar
      items={[
        {
          id: "recognition-period",
          label: copy.period,
          control: (
            <div
              id="recognition-period"
              style={{ display: "grid", gap: "8px", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
            >
              <input
                aria-label={copy.from}
                type="datetime-local"
                value={props.queryDraft.timeRange.from.slice(0, 16)}
                onChange={(event) =>
                  props.updateQueryDraft({
                    timeRange: {
                      ...props.queryDraft.timeRange,
                      from: new Date(event.target.value).toISOString(),
                    },
                  })
                }
                style={controlStyle()}
              />
              <input
                aria-label={copy.to}
                type="datetime-local"
                value={props.queryDraft.timeRange.to.slice(0, 16)}
                onChange={(event) =>
                  props.updateQueryDraft({
                    timeRange: {
                      ...props.queryDraft.timeRange,
                      to: new Date(event.target.value).toISOString(),
                    },
                  })
                }
                style={controlStyle()}
              />
            </div>
          ),
        },
        {
          id: "recognition-document-kind",
          label: copy.documentKind,
          control: (
            <input
              id="recognition-document-kind"
              value={arrayValueToText(props.queryDraft.documentKinds)}
              onChange={(event) =>
                props.updateQueryDraft({
                  documentKinds: textValueToArray(event.target.value) as AdminRecognitionAuditQuery["documentKinds"],
                })
              }
              placeholder="health_record, receipt"
              style={controlStyle()}
            />
          ),
        },
        {
          id: "recognition-status",
          label: copy.status,
          control: (
            <input
              id="recognition-status"
              value={arrayValueToText(props.queryDraft.statuses)}
              onChange={(event) =>
                props.updateQueryDraft({
                  statuses: textValueToArray(event.target.value) as AdminRecognitionAuditQuery["statuses"],
                })
              }
              placeholder="queued, failed, succeeded"
              style={controlStyle()}
            />
          ),
        },
        {
          id: "recognition-outcome",
          label: copy.outcome,
          control: (
            <input
              id="recognition-outcome"
              value={arrayValueToText(props.queryDraft.outcomes)}
              onChange={(event) =>
                props.updateQueryDraft({
                  outcomes: textValueToArray(event.target.value) as AdminRecognitionAuditQuery["outcomes"],
                })
              }
              placeholder="final_saved, failed_during_ai"
              style={controlStyle()}
            />
          ),
        },
        {
          id: "recognition-failed-stage",
          label: copy.failedStage,
          control: (
            <input
              id="recognition-failed-stage"
              value={props.queryDraft.failedStage ?? ""}
              onChange={(event) => props.updateQueryDraft({ failedStage: event.target.value || undefined })}
              style={controlStyle()}
            />
          ),
        },
        {
          id: "recognition-error-code",
          label: copy.errorCode,
          control: (
            <input
              id="recognition-error-code"
              value={props.queryDraft.errorCode ?? ""}
              onChange={(event) => props.updateQueryDraft({ errorCode: event.target.value || undefined })}
              style={controlStyle()}
            />
          ),
        },
        {
          id: "recognition-model",
          label: copy.model,
          control: (
            <input
              id="recognition-model"
              value={props.queryDraft.aiModel ?? ""}
              onChange={(event) => props.updateQueryDraft({ aiModel: event.target.value || undefined })}
              style={controlStyle()}
            />
          ),
        },
        {
          id: "recognition-account-id",
          label: copy.accountId,
          control: (
            <input
              id="recognition-account-id"
              value={props.queryDraft.accountId ?? ""}
              onChange={(event) => props.updateQueryDraft({ accountId: event.target.value || undefined })}
              style={controlStyle()}
            />
          ),
        },
        {
          id: "recognition-pet-id",
          label: copy.petId,
          control: (
            <input
              id="recognition-pet-id"
              value={props.queryDraft.petId ?? ""}
              onChange={(event) => props.updateQueryDraft({ petId: event.target.value || undefined })}
              style={controlStyle()}
            />
          ),
        },
      ]}
      actions={
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button onClick={props.onApply} style={buttonStyle("primary")} type="button">
            <Search size={16} />
            <span>{copy.apply}</span>
          </button>
          <button onClick={props.onReset} style={buttonStyle("ghost")} type="button">
            <RotateCcw size={16} />
            <span>{copy.reset}</span>
          </button>
        </div>
      }
    />
  );
}
